
import { defineComponent, PropType, ref, watch, computed, inject, ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import Checkbox from 'primevue/checkbox';
import { UserState } from '@/composables/User';
import { getTemplateAndRedirect } from '@/composables/flows/useSendingChannelForm';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { store } from '@/store';
import type { FlowStructureDto } from '@/types/flow-structure';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';

interface FunnelTemplate {
  id: number;
  name: string;
  imageUrl: string;
}

const getFirstImageKey = (imageKey: string | undefined | null): string => {
  if (!imageKey) return '';
  try {
    const parsed = JSON.parse(imageKey);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed[0];
  } catch { /* not JSON */ }
  return imageKey;
};

export default defineComponent({
  name: 'OpenEmailFunnelStepSelector',

  components: {
    Checkbox,
    TemplateHtmlPreviewSlider,
  },

  props: {
    modelValue: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
    linkedBox: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    operatorId: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const flow = inject<ComputedRef<FlowStructureDto | null>>('flow', computed(() => null));
    const funnelTemplates = ref<FunnelTemplate[]>([]);
    const loading = ref(false);
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);

    const idShop = UserState.activeShop?.id ?? 0;
    const baseImagePreviewUrl = `${process.env.VUE_APP_URL_MEDIAL_IMAGE_PREFIX_WITH_RESIZE}`;

    const getImageUrl = (imageKey: string | undefined | null): string => {
      const key = getFirstImageKey(imageKey);
      if (!key) return '';
      return `${baseImagePreviewUrl}${idShop}_${key}-isdisplay.png&w=200&${Date.now()}`;
    };

    const loadFunnelTemplates = async () => {
      if (!props.linkedBox.length || !flow.value) {
        funnelTemplates.value = [];
        return;
      }

      if (!idShop) return;

      loading.value = true;
      const templates: FunnelTemplate[] = [];
      const seenIds = new Set<number>();

      try {
        for (const stepId of props.linkedBox) {
          const step = flow.value.steps.find((s) => s.id === stepId);
          if (!step) continue;

          const params = parseOperatorParameters(step.parameters);
          const rootTemplateId = Number(params.template_display_selector_id);
          if (!rootTemplateId) continue;

          const chain = await getTemplateAndRedirect(idShop, rootTemplateId);
          if (!chain) continue;

          for (const tmpl of chain) {
            const id = Number(tmpl.id_template);
            if (seenIds.has(id)) continue;
            seenIds.add(id);

            let templateName = '';
            try {
              const config = typeof tmpl.template_config === 'string'
                ? JSON.parse(tmpl.template_config)
                : tmpl.template_config;
              templateName = config?.informations?.name || tmpl.label || `Template #${id}`;
            } catch {
              templateName = tmpl.label || `Template #${id}`;
            }

            templates.push({
              id,
              name: templateName,
              imageUrl: getImageUrl(tmpl.imageKey),
            });
          }
        }
      } catch {
        // API error — show no selector
      }

      funnelTemplates.value = templates;
      loading.value = false;

      // Cleanup : retirer de modelValue les IDs qui ne correspondent plus à un
      // template affiché (template supprimé ou retiré de la chaîne).
      if (props.modelValue.length > 0) {
        const loadedIds = new Set(templates.map((t) => t.id));
        const cleaned = props.modelValue.filter((id) => loadedIds.has(id));
        if (cleaned.length !== props.modelValue.length) {
          emit('update:modelValue', cleaned);
        }
      }
    };

    const isSelected = (id: number): boolean => props.modelValue.includes(id);

    const toggleTemplate = (id: number, checked?: boolean) => {
      const isCurrentlySelected = props.modelValue.includes(id);
      const shouldSelect = checked !== undefined ? checked : !isCurrentlySelected;
      let newValue: number[];

      if (shouldSelect && !isCurrentlySelected) {
        newValue = [...props.modelValue, id];
      } else if (!shouldSelect && isCurrentlySelected) {
        newValue = props.modelValue.filter((v) => v !== id);
      } else {
        return;
      }

      emit('update:modelValue', newValue);
    };

    const openHtmlPreview = async (templateId: number) => {
      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');
      try {
        const idTemplateB64 = btoa(String(templateId));
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/html-preview-selector',
          {},
          '',
          { idShop, idTemplate: idTemplateB64 },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        }
      } catch {
        // Preview failed silently
      }
      store.commit('general/hideTheSpinner');
    };

    watch(() => props.linkedBox, () => {
      emit('update:modelValue', []);
      loadFunnelTemplates();
    }, { deep: true });

    // Si le flow est hydraté après le mount (ex: ouverture d'une boîte existante
    // avant que le store ait peuplé flow), on retry sans reset de modelValue.
    watch(flow, (value, oldValue) => {
      if (value && !oldValue) loadFunnelTemplates();
    });

    // Initial load
    loadFunnelTemplates();

    return {
      t,
      funnelTemplates,
      isSelected,
      toggleTemplate,
      openHtmlPreview,
      htmlPreviewData,
    };
  },
});
